import 'configuration/ag-grid/ag-grid.init';
import { AgGridEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { RefObject, useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import useColumnDefinition from 'routes/smart-quote/components/prompt-results/components/quote-table/use-column-definition';
import { SmartQuoteParsingResultWithRowNumber } from 'routes/smart-quote/hooks/use-group-prompt-results';

const GRID_HEADER_HEIGHT = 43;
const DEFAULT_GRID_ROW_HEIGHT = 42;

const calculateGridHeight = (params: AgGridEvent<SmartQuoteParsingResultWithRowNumber>): number => {
  const displayedRowsCount = params.api.getDisplayedRowCount();
  let rowsHeight = 0;

  for (let index = 0; index < displayedRowsCount; index += 1) {
    const rowNode = params.api.getDisplayedRowAtIndex(index);
    rowsHeight += rowNode?.rowHeight ?? DEFAULT_GRID_ROW_HEIGHT;
  }

  return rowsHeight + GRID_HEADER_HEIGHT + 2;
};

type QuotesTableProps = {
  solveMode: string;
  results: SmartQuoteParsingResultWithRowNumber[];
  className?: string;
  gridRef?: RefObject<AgGridReact<SmartQuoteParsingResultWithRowNumber> | null>;
  initShowRowsCount?: number;
};

export const QuotesTable = ({ solveMode, results, className, gridRef, initShowRowsCount = 10 }: QuotesTableProps) => {
  const columnsDefinition = useColumnDefinition(solveMode);
  const [gridHeight, setGridHeight] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);

  const updateGridHeight = useCallback((params: AgGridEvent<SmartQuoteParsingResultWithRowNumber>) => {
    setGridHeight(calculateGridHeight(params));
  }, []);

  const filteredResults = useMemo(
    () => (!isExpanded && results.length > initShowRowsCount ? results.slice(0, initShowRowsCount) : results),
    [results, initShowRowsCount, isExpanded],
  );

  const canShowExpandButton = !isExpanded && results.length > initShowRowsCount;

  return (
    <div className={className}>
      <div style={{ height: gridHeight }}>
        <AgGridReact<SmartQuoteParsingResultWithRowNumber>
          columnDefs={columnsDefinition}
          rowData={filteredResults}
          className="ag-theme-sg-bootstrap"
          getRowId={params => params.data.rfqId}
          ref={gridRef}
          suppressMovableColumns
          onGridReady={updateGridHeight}
          onFirstDataRendered={updateGridHeight}
          onModelUpdated={updateGridHeight}
          onRowDataUpdated={updateGridHeight}
          cellSelection
          tooltipShowDelay={500}
          tooltipShowMode="whenTruncated"
        />
      </div>
      {canShowExpandButton && (
        <button className="btn btn-flat-secondary btn-lg btn-icon-end w-100" onClick={() => setIsExpanded(true)}>
          <FormattedMessage
            id="smartQuote.workspace.main.solveModeResults.showAllResultTable"
            values={{ count: results.length }}
          />
          <i className="icon">expand_more</i>
        </button>
      )}
    </div>
  );
};
